import { forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react'
import type React from 'react'

import { useKeyboardProvider } from '@/components/Keyboard/KeyboardProvider'
import { useCaretPosition } from '@/lib/useCaretPosition'

import {
  DEFAULT_COUNTRY_CODE,
  formatVatIdInput,
  getVatIdConfig,
  getVatIdInputMaxLength,
  normalizeVatId,
  VAT_ID_COUNTRY_CODES
} from './vatIdValidation'
import {
  TaxCountryButton,
  TaxCountryDropdown,
  TaxCountryOption,
  TaxInputCaret,
  TaxInputControl,
  TaxInputControlWrapper,
  TaxInputError,
  TaxInputField,
  TaxInputFieldWrapper,
  TaxInputLabel,
  TaxInputWrapper
} from './TaxInput.styles'

interface TaxInputProps {
  label: string
  value?: string | null
  countryCode?: string
  error?: string
  name?: string
  onBlur?: () => void
  onChange: (value: string) => void
  onCountryChange: (countryCode: string) => void
}

const getCountryFlag = (countryCode: string) => {
  const normalizedCountryCode = countryCode === 'GR' ? 'GR' : countryCode
  return normalizedCountryCode
    .slice(0, 2)
    .split('')
    .map(letter => String.fromCodePoint(127397 + letter.charCodeAt(0)))
    .join('')
}

const TaxInput = forwardRef<HTMLInputElement, TaxInputProps>(({ label, value, countryCode = DEFAULT_COUNTRY_CODE, error, name, onBlur, onChange, onCountryChange }, ref) => {
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const keyboardProvider = useKeyboardProvider()
  const openSharedKeyboard = keyboardProvider?.openKeyboard
  const scheduleCloseKeyboard = keyboardProvider?.scheduleCloseKeyboard
  const inputValue = formatVatIdInput(value ?? '', countryCode)
  const { prefix, placeholder } = getVatIdConfig(countryCode)
  const caretLeft = useCaretPosition(inputRef, inputValue, isFocused)

  const setRefs = (element: HTMLInputElement | null) => {
    inputRef.current = element

    if (typeof ref === 'function') {
      ref(element)
      return
    }

    if (ref) {
      ref.current = element
    }
  }

  const handleValueChange = useCallback(
    (nextValue: string) => {
      onChange(normalizeVatId(nextValue, countryCode))
    },
    [countryCode, onChange]
  )

  const openKeyboard = useCallback(() => {
    openSharedKeyboard?.({
      id: inputId,
      type: 'numericAndText',
      value: inputValue,
      maxLength: getVatIdInputMaxLength(countryCode),
      onChange: handleValueChange,
      onEnter: () => inputRef.current?.blur()
    })
  }, [countryCode, handleValueChange, inputId, inputValue, openSharedKeyboard])

  useEffect(() => {
    if (document.activeElement === inputRef.current) {
      openKeyboard()
    }
  }, [openKeyboard])

  const handleCountryChange = (nextCountryCode: string) => {
    setDropdownOpen(false)
    onCountryChange(nextCountryCode)
    onChange(normalizeVatId(value ?? '', nextCountryCode))
    window.setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }

  const handleFocus = () => {
    setIsFocused(true)
    openKeyboard()
  }

  const handleBlur = () => {
    scheduleCloseKeyboard?.(inputId)
    setIsFocused(false)
    onBlur?.()
  }

  return (
    <TaxInputWrapper data-keyboard-input-id={inputId}>
      <TaxInputLabel>{label}</TaxInputLabel>
      <TaxInputControlWrapper>
        <TaxInputControl $hasError={Boolean(error)}>
          <TaxCountryButton
            type="button"
            onPointerDown={event => event.preventDefault()}
            onClick={() => {
              setDropdownOpen(open => !open)
            }}
          >
            <span>{getCountryFlag(countryCode)}</span>
            <span>{prefix}</span>
          </TaxCountryButton>
          <TaxInputFieldWrapper>
            <TaxInputField
              ref={setRefs}
              name={name}
              value={inputValue}
              placeholder={placeholder}
              readOnly
              autoComplete="off"
              inputMode="text"
              maxLength={getVatIdInputMaxLength(countryCode)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={event => handleValueChange(event.target.value)}
            />
            {isFocused && <TaxInputCaret $left={caretLeft} />}
          </TaxInputFieldWrapper>
        </TaxInputControl>
        {dropdownOpen && (
          <TaxCountryDropdown>
            {VAT_ID_COUNTRY_CODES.map(optionCountryCode => {
              const optionConfig = getVatIdConfig(optionCountryCode)
              return (
                <TaxCountryOption
                  key={optionCountryCode}
                  type="button"
                  $active={optionCountryCode === countryCode}
                  onPointerDown={event => event.preventDefault()}
                  onClick={() => handleCountryChange(optionCountryCode)}
                >
                  <span>{getCountryFlag(optionCountryCode)}</span>
                  <span>{optionConfig.prefix}</span>
                </TaxCountryOption>
              )
            })}
          </TaxCountryDropdown>
        )}
      </TaxInputControlWrapper>
      <TaxInputError>{error}</TaxInputError>
    </TaxInputWrapper>
  )
})

TaxInput.displayName = 'TaxInput'

export default TaxInput
